import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, query, where, serverTimestamp, setDoc } from 'firebase/firestore';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, app, functions } from '../../sp-firebase/firebase';
import { SpotLockUser, UserRole, UserStatus } from '../../sp-types/userType';
import { useUser } from '../../hooks/useUser';
import { toast, ToastContainer } from 'react-toastify';
import { Edit, Key, ContentCopy, Check, ExpandMore, ExpandLess } from '@mui/icons-material';

// Role options for dropdown
const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
    { value: 'viewer', label: 'Viewer' },
    { value: 'editor', label: 'Editor' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Admin' },
    { value: 'superadmin', label: 'Super Admin' },
];

// Status options with descriptions
const STATUS_OPTIONS: { value: UserStatus; label: string; color: string; description: string }[] = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', description: 'User has not completed setup' },
    { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800 border-green-300', description: 'User can log in and use the system' },
    { value: 'suspended', label: 'Suspended', color: 'bg-red-100 text-red-800 border-red-300', description: 'Temporarily blocked from logging in' },
    { value: 'deactivated', label: 'Deactivated', color: 'bg-gray-100 text-gray-800 border-gray-300', description: 'Account is disabled' },
];

// Auth provider options
const AUTH_PROVIDER_OPTIONS = [
    { value: 'email', label: 'Email/Password' },
    { value: 'google', label: 'Google' },
    { value: 'sso', label: 'Company SSO' },
];

type AuthMethod = 'email' | 'google' | 'sso';

const AUTH_METHOD_OPTIONS = [
    { value: 'email', label: 'Email & Password', description: 'User will set password via reset link' },
    { value: 'google', label: 'Google Sign-In', description: 'User will sign in with their Google account' },
    { value: 'sso', label: 'Company SSO', description: 'User will sign in with corporate SSO' },
];

interface CreateUserForm {
    email: string;
    name: string;
    role: UserRole;
    accountId: string;
    authMethod: AuthMethod;
}

interface EditUserForm {
    id: string;
    email: string;
    name: string;
    role: UserRole;
    status: UserStatus;
    accountId: string;
    authProvider?: string;
    isEditEnabled?: boolean;
    createdAt?: any;
    lastLoginAt?: any;
    loginCount?: number;
}

export const UsersManagement: React.FC = () => {
    const { data: currentUser } = useUser();
    const [users, setUsers] = useState<SpotLockUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<UserStatus | 'all'>('all');
    const [filterRole, setFilterRole] = useState<UserRole | 'all'>('all');
    const [filterAccount, setFilterAccount] = useState<string>('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState<CreateUserForm>({
        email: '',
        name: '',
        role: 'editor',
        accountId: '',
        authMethod: 'email',
    });
    const [creating, setCreating] = useState(false);
    const [editUser, setEditUser] = useState<EditUserForm | null>(null);
    const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
    const [showMoreFields, setShowMoreFields] = useState(false);
    const [resetLinkModal, setResetLinkModal] = useState<{ show: boolean; email: string; link: string; copied: boolean; isNewUser: boolean }>({ show: false, email: '', link: '', copied: false, isNewUser: false });
    const [resettingPassword, setResettingPassword] = useState(false);

    // Fetch users
    const fetchUsers = async () => {
        setLoading(true);
        try {
            let q;
            // Superadmins see all users, others see only their account
            console.log('[USERS] Current user role:', currentUser?.role, '| accountId:', currentUser?.accountId);
            if (currentUser?.role === 'superadmin') {
                console.log('[USERS] Fetching ALL users (superadmin)');
                q = collection(db, 'users');
            } else {
                console.log('[USERS] Fetching users for account:', currentUser?.accountId);
                q = query(collection(db, 'users'), where('accountId', '==', currentUser?.accountId));
            }

            const snapshot = await getDocs(q);
            console.log('[USERS] Raw docs from Firestore:', snapshot.docs.length);

            const usersList = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id,
            })) as SpotLockUser[];

            console.log('[USERS] Processed users:', usersList.length, usersList.map(u => ({ email: u.email, accountId: u.accountId, role: u.role })));
            setUsers(usersList);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    // Fetch accounts for dropdown
    const fetchAccounts = async () => {
        try {
            const snapshot = await getDocs(collection(db, 'accounts'));
            const accountsList = snapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().companyName || doc.id,
            }));
            setAccounts(accountsList);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    useEffect(() => {
        if (currentUser?.id) {
            fetchUsers();
            fetchAccounts();
            // Set default accountId for create form
            setCreateForm(prev => ({ ...prev, accountId: currentUser.accountId }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.id]);

    // Get available roles based on current user
    // Only superadmins can assign admin or superadmin roles
    const getAvailableRoles = () => {
        if (currentUser?.role === 'superadmin') {
            return ROLE_OPTIONS;
        }
        // Non-superadmins can only assign viewer, editor, manager
        return ROLE_OPTIONS.filter(r => r.value !== 'superadmin' && r.value !== 'admin');
    };

    // Filter and sort users
    const filteredUsers = users
        .filter(user => {
            const matchesSearch =
                user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.name?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = filterStatus === 'all' ||
                user.status === filterStatus ||
                (filterStatus === 'active' && user.isApproved && !user.status);

            const matchesRole = filterRole === 'all' ||
                user.role === filterRole ||
                (filterRole === 'admin' && user.isAdmin && !user.role);

            const matchesAccount = filterAccount === 'all' || user.accountId === filterAccount;

            return matchesSearch && matchesStatus && matchesRole && matchesAccount;
        })
        .sort((a, b) => (a.email || '').localeCompare(b.email || ''));

    // Update user field
    const updateUserField = async (userId: string, field: string, value: any) => {
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                [field]: value,
                updatedAt: serverTimestamp(),
            });

            // Also update legacy fields for backwards compat
            if (field === 'status') {
                await updateDoc(userRef, { isApproved: value === 'active' });
            }
            if (field === 'role') {
                await updateDoc(userRef, { isAdmin: value === 'admin' || value === 'superadmin' });
            }

            toast.success('User updated');
            fetchUsers();
        } catch (error) {
            console.error('Error updating user:', error);
            toast.error('Failed to update user');
        }
    };

    // Send password reset - generate link via Cloud Function and show modal
    const handleSendPasswordReset = async (email: string) => {
        if (resettingPassword) return; // Prevent double-clicks
        console.log('Generating password reset link for:', email);
        setResettingPassword(true);
        try {
            console.log('[UsersManagement] Calling generatePasswordResetLink, functions:', functions);
            const generatePasswordResetLinkFn = httpsCallable(functions, 'generatePasswordResetLink');
            console.log('[UsersManagement] httpsCallable created:', generatePasswordResetLinkFn);
            const result = await generatePasswordResetLinkFn({ email });
            console.log('[UsersManagement] Function result:', result);
            const data = result.data as { success: boolean; resetLink: string };

            if (data?.success && data.resetLink) {
                setResetLinkModal({ show: true, email, link: data.resetLink, copied: false, isNewUser: false });
            } else {
                toast.error('Failed to generate reset link');
            }
        } catch (error: any) {
            console.error('Error generating password reset link:', error);
            toast.error(error.message || 'Failed to generate reset link');
        } finally {
            setResettingPassword(false);
        }
    };

    // Create new user - different flow based on auth method
    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!createForm.email || !createForm.name) {
            toast.error('Email and name are required');
            return;
        }

        setCreating(true);
        try {
            const auth = getAuth(app);
            const token = await auth.currentUser?.getIdToken();

            if (!token) {
                toast.error('Not authenticated');
                return;
            }

            const emailToShow = createForm.email;
            const accountId = createForm.accountId || currentUser?.accountId || '';

            if (createForm.authMethod === 'email') {
                // For email/password users: Call Cloud Function to create Firebase Auth user + Firestore doc
                console.log('[UsersManagement] Calling createUser, functions:', functions);
                const createUserFn = httpsCallable(functions, 'createUser');
                console.log('[UsersManagement] httpsCallable created for createUser:', createUserFn);
                const result = await createUserFn({
                    email: createForm.email.toLowerCase(),
                    name: createForm.name,
                    role: createForm.role,
                    accountId: accountId,
                    status: 'active',
                });
                const data = result.data as { success: boolean; uid: string; resetLink?: string };

                if (data?.success) {
                    setShowCreateModal(false);
                    setCreateForm({ email: '', name: '', role: 'editor', accountId: currentUser?.accountId || '', authMethod: 'email' });
                    fetchUsers();
                    if (data.resetLink) {
                        setResetLinkModal({ show: true, email: emailToShow, link: data.resetLink, copied: false, isNewUser: true });
                    } else {
                        toast.success('User created successfully!');
                    }
                } else {
                    throw new Error('Failed to create user');
                }
            } else {
                // For Google/SSO users: Create Firestore doc only (no Firebase Auth user)
                // User will be linked when they first log in via email lookup
                const newUserRef = doc(collection(db, 'users'));
                await setDoc(newUserRef, {
                    email: createForm.email.toLowerCase(),
                    name: createForm.name,
                    role: createForm.role,
                    accountId: accountId,
                    status: 'active',
                    authProvider: createForm.authMethod,
                    createdAt: serverTimestamp(),
                    createdBy: currentUser?.id || auth.currentUser?.uid,
                });

                setShowCreateModal(false);
                setCreateForm({ email: '', name: '', role: 'editor', accountId: currentUser?.accountId || '', authMethod: 'email' });
                fetchUsers();

                const methodLabel = createForm.authMethod === 'google' ? 'Google' : 'Company SSO';
                toast.success(
                    `User invited! They can now sign in using ${methodLabel} with email: ${emailToShow}`,
                    { autoClose: 6000 }
                );
            }
        } catch (error: any) {
            console.error('Error creating user:', error);
            toast.error(error.message || 'Failed to create user');
        } finally {
            setCreating(false);
        }
    };

    // Get display values with legacy fallback
    const getUserStatus = (user: SpotLockUser): UserStatus => {
        if (user.status) return user.status;
        return user.isApproved ? 'active' : 'pending';
    };

    const getUserRole = (user: SpotLockUser): UserRole => {
        if (user.role) return user.role;
        return user.isAdmin ? 'admin' : 'editor';
    };

    const getStatusColor = (status: UserStatus) => {
        return STATUS_OPTIONS.find(s => s.value === status)?.color || 'bg-gray-100';
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <ToastContainer />

            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
                    <p className="text-gray-500 text-sm mt-1">
                        {filteredUsers.length} users {filterStatus !== 'all' || filterRole !== 'all' ? '(filtered)' : ''}
                    </p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="cursor-pointer border-white text-white bg-purple text-sm border-1 py-[0.3rem] hover:brightness-95 px-[0.625rem] rounded"
                >
                    + Create User
                </button>
            </div>

            {/* Filters */}
            <div className="flex gap-3 mb-6 flex-wrap">
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
                <select
                    value={filterAccount}
                    onChange={(e) => setFilterAccount(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                >
                    <option value="all">All Accounts</option>
                    {accounts.map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                    ))}
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as UserStatus | 'all')}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                >
                    <option value="all">All Status</option>
                    {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value as UserRole | 'all')}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                >
                    <option value="all">All Roles</option>
                    {ROLE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* Users Table */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.map(user => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                                                <span className="text-purple-600 font-medium">
                                                    {(user.name || user.email || '?')[0].toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{user.name || 'No name'}</div>
                                                <div className="text-sm text-gray-500">{user.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {accounts.find(a => a.id === user.accountId)?.name || user.accountId}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span
                                            className="text-xs font-medium px-2.5 py-1 rounded-full"
                                            style={{
                                                backgroundColor: getUserRole(user) === 'superadmin' ? '#f3e8ff' :
                                                    getUserRole(user) === 'admin' ? '#fef3c7' :
                                                        getUserRole(user) === 'manager' ? '#dcfce7' : '#f3f4f6',
                                                color: getUserRole(user) === 'superadmin' ? '#7c3aed' :
                                                    getUserRole(user) === 'admin' ? '#b45309' :
                                                        getUserRole(user) === 'manager' ? '#15803d' : '#4b5563'
                                            }}
                                        >
                                            {ROLE_OPTIONS.find(r => r.value === getUserRole(user))?.label}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {(() => {
                                            const status = getUserStatus(user);
                                            const statusOpt = STATUS_OPTIONS.find(s => s.value === status);
                                            return (
                                                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusOpt?.color || 'bg-gray-100 text-gray-800'}`}>
                                                    {statusOpt?.label || status}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {user.lastLoginAt ? (
                                            <div className="flex flex-col">
                                                <span>{(user.lastLoginAt as any)?.toDate?.() ? (user.lastLoginAt as any).toDate().toLocaleDateString() : new Date(user.lastLoginAt as any).toLocaleDateString()}</span>
                                                {user.loginCount && <span className="text-xs text-gray-400">({user.loginCount} logins)</span>}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">Never</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => setEditUser({
                                                    id: user.id,
                                                    email: user.email,
                                                    name: user.name || '',
                                                    role: getUserRole(user),
                                                    status: getUserStatus(user),
                                                    accountId: user.accountId,
                                                    authProvider: user.authProvider,
                                                    isEditEnabled: user.isEditEnabled,
                                                    createdAt: user.createdAt,
                                                    lastLoginAt: user.lastLoginAt,
                                                    loginCount: user.loginCount,
                                                })}
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                                title="Edit user"
                                            >
                                                <Edit fontSize="small" />
                                            </button>
                                            <button
                                                onClick={() => handleSendPasswordReset(user.email)}
                                                className="p-1.5 text-purple-600 hover:bg-purple-50 rounded disabled:opacity-50"
                                                title="Reset password"
                                                disabled={resettingPassword}
                                            >
                                                <Key fontSize="small" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {filteredUsers.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            No users found
                        </div>
                    )}
                </div>
            )}

            {/* Create User Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">Create New User</h2>
                        <form onSubmit={handleCreateUser}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                <input
                                    type="email"
                                    value={createForm.email}
                                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    type="text"
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
                                <select
                                    value={createForm.accountId}
                                    onChange={(e) => setCreateForm({ ...createForm, accountId: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                                >
                                    {accounts.map(acc => (
                                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <select
                                    value={createForm.role}
                                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as UserRole })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                                >
                                    {getAvailableRoles().map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Login Method</label>
                                <div className="space-y-2">
                                    {AUTH_METHOD_OPTIONS.map(opt => (
                                        <label
                                            key={opt.value}
                                            className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${createForm.authMethod === opt.value
                                                ? 'border-purple-500 bg-purple-50'
                                                : 'border-gray-200 hover:bg-gray-50'
                                                }`}
                                        >
                                            <input
                                                type="radio"
                                                name="authMethod"
                                                value={opt.value}
                                                checked={createForm.authMethod === opt.value}
                                                onChange={(e) => setCreateForm({ ...createForm, authMethod: e.target.value as AuthMethod })}
                                                className="mt-0.5 mr-3"
                                            />
                                            <div>
                                                <div className="font-medium text-sm">{opt.label}</div>
                                                <div className="text-xs text-gray-500">{opt.description}</div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: '#f3f4f6' }}>
                                <p className="text-sm text-gray-600">
                                    {createForm.authMethod === 'email' && (
                                        <>📧 User will receive a password reset link to set their password.</>
                                    )}
                                    {createForm.authMethod === 'google' && (
                                        <>🔵 User will sign in with their Google account. Make sure the email matches their Google account.</>
                                    )}
                                    {createForm.authMethod === 'sso' && (
                                        <>🏢 User will sign in with Company SSO. Make sure the email matches their corporate account.</>
                                    )}
                                </p>
                            </div>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={creating}
                                    className="cursor-pointer border-white text-white bg-purple text-sm border-1 py-[0.3rem] hover:brightness-95 px-[0.625rem] rounded disabled:opacity-50"
                                >
                                    {creating ? 'Creating...' : 'Create User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {editUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">Edit User</h2>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input
                                type="email"
                                value={editUser.email}
                                disabled
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                            <input
                                type="text"
                                value={editUser.name}
                                onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
                            <select
                                value={editUser.accountId}
                                onChange={(e) => setEditUser({ ...editUser, accountId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                                disabled={currentUser?.role !== 'superadmin'}
                            >
                                {accounts.map(acc => (
                                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                                ))}
                            </select>
                            {currentUser?.role !== 'superadmin' && (
                                <p className="text-xs text-gray-400 mt-1">Only superadmins can change user accounts</p>
                            )}
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                            <select
                                value={editUser.role}
                                onChange={(e) => setEditUser({ ...editUser, role: e.target.value as UserRole })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                                disabled={editUser.id === currentUser?.id}
                            >
                                {getAvailableRoles().map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            {editUser.id === currentUser?.id && (
                                <p className="text-xs text-gray-400 mt-1">You cannot change your own role</p>
                            )}
                        </div>

                        {/* Status Toggles */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                            {editUser.id === currentUser?.id ? (
                                <p className="text-xs text-gray-400">You cannot change your own status</p>
                            ) : (
                                <div className="grid grid-cols-2 gap-2">
                                    {STATUS_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setEditUser({ ...editUser, status: opt.value })}
                                            className={`p-2 rounded-lg border-2 text-left transition-all ${editUser.status === opt.value
                                                ? `${opt.color} border-current font-medium`
                                                : 'bg-white border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className="text-sm font-medium">{opt.label}</div>
                                            <div className="text-xs opacity-75">{opt.description}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* More Fields - Expandable */}
                        <div className="mb-4 border-t pt-4">
                            <button
                                type="button"
                                onClick={() => setShowMoreFields(!showMoreFields)}
                                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                            >
                                {showMoreFields ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                {showMoreFields ? 'Hide' : 'Show'} additional fields
                            </button>

                            {showMoreFields && (
                                <div className="mt-3 space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Login Method</label>
                                        <select
                                            value={editUser.authProvider || 'email'}
                                            onChange={(e) => setEditUser({ ...editUser, authProvider: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                                        >
                                            {AUTH_PROVIDER_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={editUser.isEditEnabled ?? true}
                                                onChange={(e) => setEditUser({ ...editUser, isEditEnabled: e.target.checked })}
                                                className="w-4 h-4 text-purple-600 rounded"
                                            />
                                            <span className="text-sm text-gray-700">Can edit data</span>
                                        </label>
                                    </div>
                                    {editUser.createdAt && (
                                        <div className="text-xs text-gray-500">
                                            Created: {editUser.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                                        </div>
                                    )}
                                    {editUser.lastLoginAt && (
                                        <div className="text-xs text-gray-500">
                                            Last login: {editUser.lastLoginAt?.toDate?.()?.toLocaleDateString() || 'Never'}
                                            {editUser.loginCount ? ` (${editUser.loginCount} logins)` : ''}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setEditUser(null)}
                                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    try {
                                        const userRef = doc(db, 'users', editUser.id);
                                        await updateDoc(userRef, {
                                            name: editUser.name,
                                            role: editUser.role,
                                            status: editUser.status,
                                            accountId: editUser.accountId,
                                            authProvider: editUser.authProvider || 'email',
                                            isEditEnabled: editUser.isEditEnabled ?? true,
                                            isAdmin: editUser.role === 'admin' || editUser.role === 'superadmin',
                                            isApproved: editUser.status === 'active',
                                            updatedAt: serverTimestamp(),
                                        });
                                        toast.success('User updated');
                                        setEditUser(null);
                                        setShowMoreFields(false);
                                        fetchUsers();
                                    } catch (error) {
                                        console.error('Error updating user:', error);
                                        toast.error('Failed to update user');
                                    }
                                }}
                                className="cursor-pointer border-white text-white bg-purple text-sm border-1 py-[0.3rem] hover:brightness-95 px-[0.625rem] rounded"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Link Modal */}
            {resetLinkModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                        <h2 className="text-xl font-bold mb-2">
                            {resetLinkModal.isNewUser ? 'User Created Successfully!' : 'Password Reset Link'}
                        </h2>
                        <p className="text-gray-600 mb-4">
                            {resetLinkModal.isNewUser
                                ? <>Share this password reset link with <strong>{resetLinkModal.email}</strong> so they can set their password:</>
                                : <>Share this link with <strong>{resetLinkModal.email}</strong> to reset their password:</>
                            }
                        </p>
                        <div className="bg-gray-50 p-3 rounded-lg mb-4 break-all">
                            <code className="text-sm text-gray-700">{resetLinkModal.link}</code>
                        </div>
                        <div className="flex justify-between items-center">
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(resetLinkModal.link);
                                    setResetLinkModal({ ...resetLinkModal, copied: true });
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                {resetLinkModal.copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                                {resetLinkModal.copied ? 'Copied!' : 'Copy Link'}
                            </button>
                            <button
                                onClick={() => setResetLinkModal({ show: false, email: '', link: '', copied: false, isNewUser: false })}
                                className="cursor-pointer border-white text-white bg-purple text-sm border-1 py-2 hover:brightness-95 px-4 rounded-lg"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
